import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, Fragment, MouseEvent, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import styled, { css } from 'styled-components';

// import { ReactComponent as CalendarIcon } from '@/assets/icons/calendar.svg';
import { ReactComponent as ChatsIcon } from '@/assets/icons/chats.svg';
import { ReactComponent as HyperlinkIcon } from '@/assets/icons/hyperlink.svg';
// import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td, Thumbnail } from '@/components/Table';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import useAlerts from '@/hooks/useAlerts';
import useTableColumnViewOption from '@/hooks/useTableColumnViewOption';
import PostType from '@/pages/EditorialDistributionQueue/components/PostType';
import { tableColumns } from '@/pages/EditorialDistributionQueue/components/PostsTable';
import { EditorialFacebookPostResource } from '@/types/resources';
import { api } from '@/utils/api';
import { getNetworkLabel } from '@/utils/dq';
import {
  EditorialDistributionBenchmarkReach,
  EditorialDistributionStatus,
  EditorialFacebookPostNetwork,
  EditorialFacebookPostQueueStatus,
} from '@/utils/enums';
import { Color } from '@/utils/theme';

import { EditorialFacebookPostType } from '../../../utils/enums';
import AdsModal from './AdsModal';
import CommentsModal from './CommentsModal';
import EditForm from './EditForm';
import FacebookShareModal from './FacebookShareModal';
import RepostModal from './RepostModal';
import ScheduleModal from './ScheduleModal';

const LinkTd = styled(Td)`
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
`;

const ChannelHolder = styled.div`
  display: flex;
  align-items: center;
`;

const ThumbnailTd = styled(Td)`
  ${Thumbnail} {
    max-width: 89px;
  }
`;

const HyperLink = styled('a').attrs({
  rel: 'noreferrer noopener',
  target: '_blank',
})`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  svg {
    margin-right: 6px;
    width: 14px;
    flex-shrink: 0;
  }
  > span {
    overflow-x: hidden;
    text-overflow: ellipsis;
  }
`;

const Time = styled(Text)`
  margin-top: 4px;
`;

const Image = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 6px;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const ChannelName = styled.span`
  white-space: nowrap;
`;

const Indicator = styled.div`
  display: flex;
  align-items: center;
`;

const Dot = styled.div<{ $color: Color }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 8px;
  background: ${({ $color, theme }) => theme.colors[$color]};
`;

const QueueBadge = styled(Text).attrs({
  $type: 'extraSmall',
  $color: 'gray',
  as: 'div',
})`
  display: inline-block;
  padding: 3px 4px;
  border-radius: 4px;
  margin-top: 8px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
`;

const CommentsButton = styled.button<{ $disabled: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.success};
  background-color: ${({ theme }) => theme.colors.successLight};
  border: solid 1px transparent;
  transition: border-color ease-out 0.2s;
  svg {
    width: 12px;
  }
  &:hover,
  &:active {
    border-color: ${({ theme }) => theme.colors.success};
  }
  ${({ theme, $disabled }) =>
    $disabled &&
    css`
      color: ${theme.colors.gray};
      background-color: ${theme.colors.grayscaleLight};
      &:hover {
        border-color: ${theme.colors.gray};
      }
    `}
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
`;

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 180px;
`;

const DepartmentsList = styled.ul`
  list-style-type: disc;
  list-style-position: inside;
`;

export const NetworkLabel = styled.span`
  text-transform: capitalize;
`;

type FreshBadgeProps = { $isFresh: boolean };

const FreshBadge = styled(Text).attrs({
  $type: 'extraSmall',
  as: 'div',
})<FreshBadgeProps>`
  display: inline-block;
  padding: 4.5px;
  border-radius: 4px;
  margin-top: 8px;
  background: #fbf1f0;
  color: #c9513d;

  ${({ $isFresh }) =>
    $isFresh &&
    css`
      background: #edf4f0;
      color: #18713d;
    `}
`;

interface Props {
  schedule: EditorialFacebookPostResource;
  columns?: string[];
}

const EditorialDistributionTableRow: FC<Props> = ({ schedule, columns }) => {
  const [searchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showAdsModal, setShowAdsModal] = useState(false);
  const [showRepostForm, setShowRepostForm] = useState(false);
  const [showFacebookShareModal, setShowFacebookShareModal] = useState(false);
  const [showCommentsCheckModal, setShowCommentsCheckModal] = useState(
    searchParams.get('search') === schedule.externalId &&
      !!searchParams.get('commentCheck'),
  );
  // eslint-disable-next-line
  const [_, networkLabel] = useNetwork();

  const navigate = useNavigate();

  const alerts = useAlerts();
  const { status } = useParams();
  const location = useLocation();

  const tableViewOptionsKey = location.pathname.includes('distribution-queue')
    ? `${status!}_${networkLabel}_dq_posts_table_v4`
    : 'editorialDistributionTable';

  const getColumnVisibility = useTableColumnViewOption(
    tableViewOptionsKey,
    tableColumns,
  );

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to delete this post?`,
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/editorial/facebook-posts/${schedule.id}`,
      );

      await queryClient.invalidateQueries(
        ['editorial_distribution_schedules'],
        {
          exact: false,
        },
      );

      alerts.success('Post was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  const renderStatus = () => {
    if (schedule.deletedAt) {
      return (
        <Indicator>
          <Dot $color={'error'} />
          Deleted
        </Indicator>
      );
    }

    if (schedule.queueStatus === EditorialFacebookPostQueueStatus.FAILED) {
      return (
        <Indicator>
          <Dot $color={'error'} />
          Failed
        </Indicator>
      );
    }

    if (schedule.status === EditorialDistributionStatus.SCHEDULED) {
      return (
        <Indicator>
          <Dot $color={'warning'} />
          Scheduled
        </Indicator>
      );
    }

    return (
      <Indicator>
        <Dot $color={'success'} />
        Posted
      </Indicator>
    );
  };

  const renderBenchmarkReach = () => {
    if (
      schedule.benchmarkReach ===
      EditorialDistributionBenchmarkReach.NOT_REACHED
    ) {
      return (
        <Indicator>
          <Dot $color={'error'} />
          Not Reached
        </Indicator>
      );
    }

    if (
      schedule.benchmarkReach === EditorialDistributionBenchmarkReach.REACHED
    ) {
      return (
        <Indicator>
          <Dot $color={'success'} />
          Reached
        </Indicator>
      );
    }

    return (
      <Indicator>
        <Dot $color={'gray'} />
        Pending
      </Indicator>
    );
  };

  const renderIsFreshBadge = () => {
    if (schedule.isFresh === null) {
      return null;
    }

    return (
      <>
        <br />
        <FreshBadge $isFresh={!!schedule.isFresh}>
          {schedule.isFresh ? 'Fresh' : 'Reuse'}
        </FreshBadge>
      </>
    );
  };

  const postHasLinkStats = [
    EditorialFacebookPostType.LINK,
    EditorialFacebookPostType.IMAGE,
  ].includes(schedule.type);

  const showCommentsButton =
    schedule.queueStatus === EditorialFacebookPostQueueStatus.POSTED &&
    schedule.network === EditorialFacebookPostNetwork.FACEBOOK &&
    [
      EditorialFacebookPostType.IMAGE,
      EditorialFacebookPostType.LINK,
      EditorialFacebookPostType.TEXT,
    ].includes(schedule.type);

  const renderRow = () => {
    return (columns ?? tableColumns.map((col) => col.title)).map((column) => {
      switch (column) {
        case 'Network':
          return (
            getColumnVisibility('Network') && (
              <Fragment key={column}>
                <Td>
                  <NetworkLabel>
                    {getNetworkLabel(schedule.network)}
                  </NetworkLabel>
                </Td>
              </Fragment>
            )
          );

        case 'Page Name':
          return (
            getColumnVisibility('Page Name') && (
              <Fragment key={column}>
                <Td>
                  <ChannelHolder>
                    {!!schedule.channel.thumbnail && (
                      <Image
                        src={schedule.channel.thumbnail}
                        alt={schedule.channel.name + ' thumbnail'}
                      />
                    )}
                    <ChannelName>{schedule.channel.name}</ChannelName>
                  </ChannelHolder>
                </Td>
              </Fragment>
            )
          );

        case 'Content Type':
          return (
            getColumnVisibility('Content Type') && (
              <Fragment key={column}>
                <Td>
                  <PostType type={schedule.type} />
                </Td>
              </Fragment>
            )
          );

        case 'Status':
          return (
            getColumnVisibility('Status') && (
              <Fragment key={column}>
                <Td>
                  {renderStatus()}
                  {!!schedule.queueStatus && <QueueBadge>Queued</QueueBadge>}
                </Td>
              </Fragment>
            )
          );

        case 'Benchmark Reach':
          return (
            getColumnVisibility('Benchmark Reach') && (
              <Fragment key={column}>
                <Td>{renderBenchmarkReach()}</Td>
              </Fragment>
            )
          );

        case 'Deletion Date':
          return (
            getColumnVisibility('Deletion Date') && (
              <Fragment key={column}>
                <Td>
                  {schedule.deletedAt ? (
                    <>
                      {DateTime.fromISO(schedule.deletedAt).toLocaleString()}{' '}
                      <br />
                      <Time $color="gray" $type="extraSmall">
                        {DateTime.fromISO(schedule.deletedAt).toFormat('HH:mm')}
                      </Time>
                    </>
                  ) : (
                    '-'
                  )}
                </Td>
              </Fragment>
            )
          );

        case 'Schedule Date':
          return (
            getColumnVisibility('Schedule Date') && (
              <Fragment key={column}>
                <Td>
                  {DateTime.fromISO(schedule.createdAt).toLocaleString()} <br />
                  <Time $color="gray" $type="extraSmall">
                    {DateTime.fromISO(schedule.createdAt).toFormat('HH:mm')}
                  </Time>
                </Td>
              </Fragment>
            )
          );

        case 'Post Date':
          return (
            getColumnVisibility('Post Date') && (
              <Fragment key={column}>
                <Td>
                  {schedule.publishedAt ? (
                    <>
                      {DateTime.fromISO(schedule.publishedAt).toLocaleString()}{' '}
                      <br />
                      <Time $color="gray" $type="extraSmall">
                        {DateTime.fromISO(schedule.publishedAt).toFormat(
                          'HH:mm',
                        )}
                      </Time>
                    </>
                  ) : (
                    '-'
                  )}
                </Td>
              </Fragment>
            )
          );

        case 'Site Visits':
          return (
            getColumnVisibility('Site Visits') && (
              <Fragment key={column}>
                <Td>{!postHasLinkStats ? '-' : schedule.siteVisits ?? '-'}</Td>
              </Fragment>
            )
          );

        case 'Site Visit Benchmark':
          return (
            getColumnVisibility('Site Visit Benchmark') && (
              <Fragment key={column}>
                <Td>{!postHasLinkStats ? '-' : schedule.siteVisitBenchmark}</Td>
              </Fragment>
            )
          );

        case 'Reach Duration':
          return (
            getColumnVisibility('Reach Duration') && (
              <Fragment key={column}>
                <Td>{!postHasLinkStats ? '-' : schedule.reachDuration}</Td>
              </Fragment>
            )
          );

        case 'Departments':
          return (
            getColumnVisibility('Departments') && (
              <Fragment key={column}>
                <Td>
                  {!!schedule.departments?.length ? (
                    <DepartmentsList>
                      {schedule.departments.map((dep) => (
                        <li key={dep.id}>{dep.name}</li>
                      ))}
                    </DepartmentsList>
                  ) : (
                    '-'
                  )}
                </Td>
              </Fragment>
            )
          );

        case 'Reactions':
          return (
            getColumnVisibility('Reactions') && (
              <Fragment key={column}>
                <Td>{schedule.reactions ?? '-'}</Td>
              </Fragment>
            )
          );

        case 'Comments':
          return (
            getColumnVisibility('Comments') && (
              <Fragment key={column}>
                <Td>{schedule.comments ?? '-'}</Td>
              </Fragment>
            )
          );

        case 'Shares':
          return (
            getColumnVisibility('Shares') && (
              <Fragment key={column}>
                <Td>{schedule.shares ?? '-'}</Td>
              </Fragment>
            )
          );

        case 'Article Link':
          return (
            getColumnVisibility('Article Link') && (
              <Fragment key={column}>
                <LinkTd>
                  {schedule.link ? (
                    <>
                      <HyperLink href={schedule.link}>
                        <HyperlinkIcon />
                        <span>{schedule.link}</span>
                      </HyperLink>
                      {renderIsFreshBadge()}
                    </>
                  ) : (
                    '-'
                  )}
                </LinkTd>
              </Fragment>
            )
          );

        case 'Post Link':
          return (
            getColumnVisibility('Post Link') && (
              <Fragment key={column}>
                <LinkTd>
                  {schedule.facebookLink ? (
                    <HyperLink href={schedule.facebookLink}>
                      <HyperlinkIcon />
                      <span>{schedule.facebookLink}</span>
                    </HyperLink>
                  ) : (
                    '-'
                  )}
                </LinkTd>
              </Fragment>
            )
          );

        case 'Owner':
          return (
            getColumnVisibility('Owner') && (
              <Fragment key={column}>
                <Td>{schedule.owner}</Td>
              </Fragment>
            )
          );

        case 'Notes':
          return (
            getColumnVisibility('Notes') && (
              <Fragment key={column}>
                <Td>{schedule.notes ?? '-'}</Td>
              </Fragment>
            )
          );

        case '24h Link Visits':
          return (
            getColumnVisibility('24h Link Visits') && (
              <Fragment key={column}>
                <Td>{schedule.stats?.h24?.link_visits ?? '-'}</Td>
              </Fragment>
            )
          );

        case '24h Post Comments':
          return (
            getColumnVisibility('24h Post Comments') && (
              <Fragment key={column}>
                <Td>{schedule.stats?.h24?.post_comments ?? '-'}</Td>
              </Fragment>
            )
          );

        case '24h Post Reactions':
          return (
            getColumnVisibility('24h Post Reactions') && (
              <Fragment key={column}>
                <Td>{schedule.stats?.h24?.post_reactions ?? '-'}</Td>
              </Fragment>
            )
          );

        case '24h Post Shares':
          return (
            getColumnVisibility('24h Post Shares') && (
              <Fragment key={column}>
                <Td>{schedule.stats?.h24?.post_shares ?? '-'}</Td>
              </Fragment>
            )
          );

        case '24h Post Clicks':
          return (
            getColumnVisibility('24h Post Clicks') && (
              <Fragment key={column}>
                <Td>{schedule.stats?.h24?.post_clicks ?? '-'}</Td>
              </Fragment>
            )
          );

        case '24h Post Impressions':
          return (
            getColumnVisibility('24h Post Impressions') && (
              <Fragment key={column}>
                <Td>{schedule.stats?.h24?.post_impressions ?? '-'}</Td>
              </Fragment>
            )
          );

        case '24h CTR':
          return (
            getColumnVisibility('24h CTR') && (
              <Fragment key={column}>
                <Td>
                  {schedule.stats?.h24?.ctr !== null
                    ? `${schedule.stats?.h24?.ctr}%`
                    : '-'}
                </Td>
              </Fragment>
            )
          );

        case '24h Engagement Rate':
          return (
            getColumnVisibility('24h Engagement Rate') && (
              <Fragment key={column}>
                <Td>
                  {schedule.stats?.h24?.engagement_ratio !== null
                    ? `${schedule.stats?.h24?.engagement_ratio}%`
                    : '-'}
                </Td>
              </Fragment>
            )
          );
        case '24h Video Views':
          return (
            getColumnVisibility('24h Video Views') && (
              <Fragment key={column}>
                <Td>{schedule.stats?.h24?.video_views ?? '-'}</Td>
              </Fragment>
            )
          );

        case '1h Link Visits':
          return (
            getColumnVisibility('1h Link Visits') && (
              <Fragment key={column}>
                <Td>{schedule.stats?.h1?.link_visits ?? '-'}</Td>
              </Fragment>
            )
          );

        case '1h Post Comments':
          return (
            getColumnVisibility('1h Post Comments') && (
              <Fragment key={column}>
                <Td>{schedule.stats?.h1?.post_comments ?? '-'}</Td>
              </Fragment>
            )
          );

        case '1h Post Reactions':
          return (
            getColumnVisibility('1h Post Reactions') && (
              <Fragment key={column}>
                <Td>{schedule.stats?.h1?.post_reactions ?? '-'}</Td>
              </Fragment>
            )
          );

        case '1h Post Shares':
          return (
            getColumnVisibility('1h Post Shares') && (
              <Fragment key={column}>
                <Td>{schedule.stats?.h1?.post_shares ?? '-'}</Td>
              </Fragment>
            )
          );

        case '1h Post Clicks':
          return (
            getColumnVisibility('1h Post Clicks') && (
              <Fragment key={column}>
                <Td>{schedule.stats?.h1?.post_clicks ?? '-'}</Td>
              </Fragment>
            )
          );

        case '1h Post Impressions':
          return (
            getColumnVisibility('1h Post Impressions') && (
              <Fragment key={column}>
                <Td>{schedule.stats?.h1?.post_impressions ?? '-'}</Td>
              </Fragment>
            )
          );

        case '1h CTR':
          return (
            getColumnVisibility('1h CTR') && (
              <Fragment key={column}>
                <Td>
                  {schedule.stats?.h1?.ctr !== null
                    ? `${schedule.stats?.h1?.ctr}%`
                    : '-'}
                </Td>
              </Fragment>
            )
          );

        case '1h Engagement Rate':
          return (
            getColumnVisibility('1h Engagement Rate') && (
              <Fragment key={column}>
                <Td>
                  {schedule.stats?.h1?.engagement_ratio !== null
                    ? `${schedule.stats?.h1?.engagement_ratio}%`
                    : '-'}
                </Td>
              </Fragment>
            )
          );

        case '1h Video Views':
          return (
            getColumnVisibility('1h Video Views') && (
              <Fragment key={column}>
                <Td>{schedule.stats?.h1?.video_views ?? '-'}</Td>
              </Fragment>
            )
          );

        case 'Comments Checker Disabled':
          return (
            getColumnVisibility('Comments Checker Disabled') && (
              <Fragment key={column}>
                <Td>{schedule.commentsCheckerDisabled ? 'Yes' : 'No'}</Td>
              </Fragment>
            )
          );
        case 'Error Message':
          return (
            getColumnVisibility('Error Message') && (
              <Fragment key={column}>
                <Td>{schedule.error ?? '-'}</Td>
              </Fragment>
            )
          );

        default:
          return null;
      }
    });
  };

  const handleFailedPostRestore = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure?',
        'Put this failed post back to the queue',
      );

      if (!confirmed) {
        return;
      }

      await api.post(`/v1/editorial/facebook-posts/${schedule.id}/queue`);

      await queryClient.invalidateQueries(['editorial_distribution_schedules']);

      alerts.close();
    } catch {
      alerts.error();
    }
  };

  return (
    <>
      <tr>
        {getColumnVisibility('Cover') && (
          <ThumbnailTd>
            <Thumbnail
              src={schedule.imageUrl}
              alt={'Cover'}
              type={
                schedule.type === EditorialFacebookPostType.REEL
                  ? 'reel'
                  : 'image'
              }
            />
          </ThumbnailTd>
        )}
        {getColumnVisibility('Message') && <Td>{schedule.message}</Td>}
        {renderRow()}
        {/* 
        {getColumnVisibility('Network') && (
          <Td>
            <NetworkLabel>{getNetworkLabel(schedule.network)}</NetworkLabel>
          </Td>
        )}
        {getColumnVisibility('Page Name') && (
          <Td>
            <ChannelHolder>
              {!!schedule.channel.thumbnail && (
                <Image
                  src={schedule.channel.thumbnail}
                  alt={schedule.channel.name + ' thumbnail'}
                />
              )}
              <ChannelName>{schedule.channel.name}</ChannelName>
            </ChannelHolder>
          </Td>
        )}
        {getColumnVisibility('Content Type') && (
          <Td>
            <PostType type={schedule.type} />
          </Td>
        )}
        {getColumnVisibility('Status') && (
          <Td>
            {renderStatus()}
            {!!schedule.queueStatus && <QueueBadge>Queued</QueueBadge>}
          </Td>
        )}

        {getColumnVisibility('Benchmark Reach') && (
          <Td>{renderBenchmarkReach()}</Td>
        )}

        {getColumnVisibility('Deletion Date') && (
          <Td>
            {schedule.deletedAt ? (
              <>
                {DateTime.fromISO(schedule.deletedAt).toLocaleString()} <br />
                <Time $color="gray" $type="extraSmall">
                  {DateTime.fromISO(schedule.deletedAt).toFormat('HH:mm')}
                </Time>
              </>
            ) : (
              '-'
            )}
          </Td>
        )}
        {getColumnVisibility('Schedule Date') && (
          <Td>
            {DateTime.fromISO(schedule.createdAt).toLocaleString()} <br />
            <Time $color="gray" $type="extraSmall">
              {DateTime.fromISO(schedule.createdAt).toFormat('HH:mm')}
            </Time>
          </Td>
        )}
        {getColumnVisibility('Post Date') && (
          <Td>
            {schedule.publishedAt ? (
              <>
                {DateTime.fromISO(schedule.publishedAt).toLocaleString()} <br />
                <Time $color="gray" $type="extraSmall">
                  {DateTime.fromISO(schedule.publishedAt).toFormat('HH:mm')}
                </Time>
              </>
            ) : (
              '-'
            )}
          </Td>
        )}
        {getColumnVisibility('Site Visits') && (
          <Td>{!postHasLinkStats ? '-' : schedule.siteVisits ?? '-'}</Td>
        )}
        {getColumnVisibility('Site Visit Benchmark') && (
          <Td>{!postHasLinkStats ? '-' : schedule.siteVisitBenchmark}</Td>
        )}
        {getColumnVisibility('Reach Duration') && (
          <Td>{!postHasLinkStats ? '-' : schedule.reachDuration}</Td>
        )}
        {getColumnVisibility('Departments') && (
          <Td>
            {!!schedule.departments?.length ? (
              <DepartmentsList>
                {schedule.departments.map((dep) => (
                  <li key={dep.id}>{dep.name}</li>
                ))}
              </DepartmentsList>
            ) : (
              '-'
            )}
          </Td>
        )}
        {getColumnVisibility('Reactions') && (
          <Td>{schedule.reactions ?? '-'}</Td>
        )}
        {getColumnVisibility('Comments') && <Td>{schedule.comments ?? '-'}</Td>}
        {getColumnVisibility('Shares') && <Td>{schedule.shares ?? '-'}</Td>}

        {getColumnVisibility('Article Link') && (
          <LinkTd>
            {schedule.link ? (
              <>
                <HyperLink href={schedule.link}>
                  <HyperlinkIcon />
                  <span>{schedule.link}</span>
                </HyperLink>
                {renderIsFreshBadge()}
              </>
            ) : (
              '-'
            )}
          </LinkTd>
        )}
        {getColumnVisibility('Post Link') && (
          <LinkTd>
            {schedule.facebookLink ? (
              <HyperLink href={schedule.facebookLink}>
                <HyperlinkIcon />
                <span>{schedule.facebookLink}</span>
              </HyperLink>
            ) : (
              '-'
            )}
          </LinkTd>
        )}
        {getColumnVisibility('Owner') && <Td>{schedule.owner}</Td>}
        {getColumnVisibility('Notes') && <Td>{schedule.notes ?? '-'}</Td>}
        {getColumnVisibility('24h Link Visits') && (
          <Td>{schedule.stats?.h24?.link_visits ?? '-'}</Td>
        )}
        {getColumnVisibility('24h Post Comments') && (
          <Td>{schedule.stats?.h24?.post_comments ?? '-'}</Td>
        )}
        {getColumnVisibility('24h Post Reactions') && (
          <Td>{schedule.stats?.h24?.post_reactions ?? '-'}</Td>
        )}
        {getColumnVisibility('24h Post Shares') && (
          <Td>{schedule.stats?.h24?.post_shares ?? '-'}</Td>
        )}
        {getColumnVisibility('24h Post Clicks') && (
          <Td>{schedule.stats?.h24?.post_clicks ?? '-'}</Td>
        )}
        {getColumnVisibility('24h Post Impressions') && (
          <Td>{schedule.stats?.h24?.post_impressions ?? '-'}</Td>
        )}
        {getColumnVisibility('24h CTR') && (
          <Td>
            {schedule.stats?.h24?.ctr ? `${schedule.stats?.h24?.ctr}%` : '-'}
          </Td>
        )}
        {getColumnVisibility('24h Engagement Rate') && (
          <Td>
            {schedule.stats?.h24?.engagement_rate
              ? `${schedule.stats?.h24?.engagement_rate}%`
              : '-'}
          </Td>
        )}
        {getColumnVisibility('1h Link Visits') && (
          <Td>{schedule.stats?.h1?.link_visits ?? '-'}</Td>
        )}
        {getColumnVisibility('1h Post Comments') && (
          <Td>{schedule.stats?.h1?.post_comments ?? '-'}</Td>
        )}
        {getColumnVisibility('1h Post Reactions') && (
          <Td>{schedule.stats?.h1?.post_reactions ?? '-'}</Td>
        )}
        {getColumnVisibility('1h Post Shares') && (
          <Td>{schedule.stats?.h1?.post_shares ?? '-'}</Td>
        )}
        {getColumnVisibility('1h Post Clicks') && (
          <Td>{schedule.stats?.h1?.post_clicks ?? '-'}</Td>
        )}
        {getColumnVisibility('1h Post Impressions') && (
          <Td>{schedule.stats?.h1?.post_impressions ?? '-'}</Td>
        )}
        {getColumnVisibility('1h CTR') && (
          <Td>
            {schedule.stats?.h1?.ctr ? `${schedule.stats?.h1?.ctr}%` : '-'}
          </Td>
        )}
        {getColumnVisibility('1h Engagement Rate') && (
          <Td>
            {schedule.stats?.h1?.engagement_rate
              ? `${schedule.stats?.h1?.engagement_rate}%`
              : '-'}
          </Td>
        )}
        {getColumnVisibility('Error Message') && (
          <Td>{schedule.error ?? '-'}</Td>
        )} */}
        {!schedule.deletedAt && (
          <Td $actionsCol>
            <Actions>
              {showCommentsButton && (
                <CommentsButton
                  $disabled={!schedule.hasComments}
                  onClick={() =>
                    navigate(`/general/post-distribution/${schedule.id}`)
                  }
                >
                  <ChatsIcon />
                </CommentsButton>
              )}
              <ActionsWrap>
                <StyledActionsDropdown>
                  {schedule.network ===
                    EditorialFacebookPostNetwork.FACEBOOK && (
                    <>
                      <ActionsDropdownButton
                        as="button"
                        onClick={() => setShowRepostForm(true)}
                      >
                        <span>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M11 9.21094L14.4152 5.69531L11 2.17969"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M11.7631 13.8984H5.50195C4.44523 13.8984 3.43179 13.4663 2.68457 12.6971C1.93736 11.9279 1.51758 10.8847 1.51758 9.79688C1.51758 9.25825 1.62064 8.72489 1.82087 8.22727C2.0211 7.72965 2.31459 7.27749 2.68457 6.89663C3.05455 6.51577 3.49379 6.21365 3.97719 6.00752C4.4606 5.8014 4.97871 5.69531 5.50195 5.69531H14.1783"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        Repost
                      </ActionsDropdownButton>
                    </>
                  )}
                  {[
                    EditorialFacebookPostNetwork.FACEBOOK,
                    EditorialFacebookPostNetwork.REELS,
                  ].includes(schedule.network) && (
                    <ActionsDropdownButton
                      as="button"
                      onClick={() => setShowAdsModal(true)}
                    >
                      <span>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M14.4167 2H1.58333C1.26117 2 1 2.26863 1 2.6V13.4C1 13.7314 1.26117 14 1.58333 14H14.4167C14.7388 14 15 13.7314 15 13.4V2.6C15 2.26863 14.7388 2 14.4167 2Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10.6152 11.0363C10.2074 11.0363 9.85011 10.9336 9.5435 10.7283C9.2369 10.523 8.99737 10.2438 8.8249 9.89062C8.65518 9.53748 8.57031 9.13917 8.57031 8.69568C8.57031 8.24399 8.65654 7.84294 8.82901 7.49253C9.00421 7.13939 9.24922 6.86153 9.56404 6.65895C9.87885 6.45637 10.2484 6.35508 10.6727 6.35508C11.0943 6.35508 11.4488 6.45774 11.7363 6.66306C12.0237 6.86837 12.2413 7.1476 12.3892 7.50075C12.537 7.85389 12.6109 8.2522 12.6109 8.69568C12.6109 9.13917 12.5356 9.53748 12.3851 9.89062C12.2372 10.2438 12.0155 10.523 11.7198 10.7283C11.4242 10.9336 11.056 11.0363 10.6152 11.0363ZM10.7959 10.0426C11.045 10.0426 11.2435 9.98644 11.3913 9.8742C11.5419 9.76196 11.65 9.60455 11.7157 9.40197C11.7814 9.19939 11.8143 8.96396 11.8143 8.69568C11.8143 8.4274 11.7814 8.19198 11.7157 7.9894C11.65 7.78682 11.5446 7.62941 11.3995 7.51717C11.2572 7.40493 11.0697 7.34881 10.837 7.34881C10.5879 7.34881 10.3826 7.41041 10.221 7.5336C10.0623 7.65405 9.94455 7.81693 9.8679 8.02225C9.79125 8.22483 9.75293 8.44931 9.75293 8.69568C9.75293 8.9448 9.78988 9.17202 9.86379 9.37733C9.93771 9.57991 10.0513 9.74143 10.2046 9.86188C10.3579 9.98233 10.555 10.0426 10.7959 10.0426ZM11.8143 10.9131V7.87442H11.6747V5H12.7998V10.9131H11.8143Z"
                            fill="currentColor"
                          />
                          <path
                            d="M2.7998 10.9131L4.60657 5H6.27373L8.0805 10.9131H6.93074L5.32107 5.69807H5.5346L3.94957 10.9131H2.7998ZM3.88387 9.6812V8.6382H7.00465V9.6812H3.88387Z"
                            fill="currentColor"
                          />
                        </svg>
                      </span>
                      Ads
                    </ActionsDropdownButton>
                  )}
                  {schedule.queueStatus ===
                    EditorialFacebookPostQueueStatus.POSTED && (
                    <ActionsDropdownButton
                      as="button"
                      onClick={() => setShowFacebookShareModal(true)}
                    >
                      <span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="size-6"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z"
                          />
                        </svg>
                      </span>
                      Share on Facebook
                    </ActionsDropdownButton>
                  )}
                  {schedule.queueStatus ===
                    EditorialFacebookPostQueueStatus.FAILED && (
                    <ActionsDropdownButton
                      as="button"
                      onClick={handleFailedPostRestore}
                    >
                      <span>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M11 9.21094L14.4152 5.69531L11 2.17969"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M11.7631 13.8984H5.50195C4.44523 13.8984 3.43179 13.4663 2.68457 12.6971C1.93736 11.9279 1.51758 10.8847 1.51758 9.79688C1.51758 9.25825 1.62064 8.72489 1.82087 8.22727C2.0211 7.72965 2.31459 7.27749 2.68457 6.89663C3.05455 6.51577 3.49379 6.21365 3.97719 6.00752C4.4606 5.8014 4.97871 5.69531 5.50195 5.69531H14.1783"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      Restore
                    </ActionsDropdownButton>
                  )}
                  <ActionsDropdownButton as="button" onClick={onDelete}>
                    <span>
                      <TrashIcon />
                    </span>
                    Delete
                  </ActionsDropdownButton>
                </StyledActionsDropdown>
              </ActionsWrap>
            </Actions>
          </Td>
        )}
        <AnimatePresence>
          {showModal && (
            <ScheduleModal
              schedule={schedule}
              onClose={() => setShowModal(false)}
            />
          )}
          {showEditForm && (
            <EditForm post={schedule} onClose={() => setShowEditForm(false)} />
          )}
          {showAdsModal && (
            <AdsModal onClose={() => setShowAdsModal(false)} post={schedule} />
          )}
          {showRepostForm && (
            <RepostModal
              onClose={() => setShowRepostForm(false)}
              post={schedule}
            />
          )}
          {showCommentsCheckModal && (
            <CommentsModal
              onClose={() => setShowCommentsCheckModal(false)}
              post={schedule}
            />
          )}
          {showFacebookShareModal && (
            <FacebookShareModal
              onClose={() => setShowFacebookShareModal(false)}
              postId={schedule.id}
            />
          )}
        </AnimatePresence>
      </tr>
    </>
  );
};

export default EditorialDistributionTableRow;
